import { FormikProvider, useFormik } from 'formik';
import { HuddleCashBudget } from 'lib/generated/graphql';
import { forwardRef, useImperativeHandle } from 'react';
import { Select, Toggle } from 'src/shared-components/Form';
import { enumToDescriptedArray } from 'utils/common';
import * as Yup from 'yup';
import options from './FormBudgetOptions';

const budgetOptions = {
  preferredStartDates: [
    {
      value: 'ASAP',
      label: 'ASAP',
    },
    {
      value: 'NEXT_MONTH',
      label: 'In the next month',
    },
    {
      value: 'NEXT_COUPLE_OF_MONTHS',
      label: 'In the next three months',
    },
  ],
};
interface FormBudgetProps {
  onFormSubmitted: (values: any) => void;
  initialValues: any;
  update: boolean;
}

const FormBudget = forwardRef(({ onFormSubmitted, initialValues, update = false }: FormBudgetProps, ref) => {
  const form = useFormik({
    initialValues,
    validationSchema: Yup.object({
      preferredStartDate: Yup.string().required('Hey we need this to get going'),
      preferredDuration: Yup.number(),
      cashBudget: Yup.string().required('Please select an option, we need this to get going'),
      equityOrCrypto: Yup.bool().required("We need to know if you're willing to pay with equity or crypto"),
    }),
    onSubmit: values => {
      /* istanbul ignore next */
      if (update) {
        onFormSubmitted({
          preferredStartDate: { set: values.preferredStartDate },
          preferredDuration: { set: values.preferredDuration },
          cashBudget: { set: values.cashBudget },
          equityOrCrypto: { set: values.equityOrCrypto },
        });
      } else {
        onFormSubmitted({
          preferredStartDate: values.preferredStartDate,
          preferredDuration: values.preferredDuration,
          cashBudget: values.cashBudget,
          equityOrCrypto: values.equityOrCrypto,
        });
      }
    },
  });

  useImperativeHandle(ref, () => ({
    submitForm() {
      form.submitForm();
    },
  }));

  return (
    <FormikProvider value={form}>
      <div data-testid="form-budget" className="grid grid-cols-6 gap-4 text-left">
        <div className="col-span-6 sm:col-span-6 md:col-span-3">
          <Select
            data-testid="preferredStartDate"
            options={budgetOptions.preferredStartDates}
            label="Preferred start date"
            name="preferredStartDate"
            required
            placeholder="Select one..."
          />
        </div>
        <div className="col-span-6 sm:col-span-6 md:col-span-3">
          <Select
            data-testid="preferredDuration"
            options={options.durations}
            label="Preferred duration"
            name="preferredDuration"
            placeholder="Select one..."
            onChange={e => {
              /* istanbul ignore next */
              form.setFieldValue('preferredDuration', parseInt(e.target.value));
            }}
          />
        </div>
        <div className="col-span-6 sm:col-span-6">
          <Select
            data-testid="cashBudget"
            options={enumToDescriptedArray(HuddleCashBudget)
              .filter(item => item.id !== HuddleCashBudget.Range_10000_150000)
              .map(item => ({
                value: item.id,
                label: item.description.replace('Range ', '$').replace(/ /g, '-$').replace('Plus', '+'),
              }))} // TODO: create a method to format the numbers
            label="Cash budget"
            name="cashBudget"
            required
            placeholder="Select one..."
          />
        </div>
        <div className="col-span-6 sm:col-span-6">
          <Toggle
            onChange={value => {
              /* istanbul ignore next */
              form.setFieldValue('equityOrCrypto', value);
            }}
            label="Open to paying partly in equity or crypto."
            checked
          />
        </div>
      </div>
    </FormikProvider>
  );
});

export default FormBudget;
